import { Vector3 } from 'three'
import { KVAnnotation, WorldPosition } from 'types'

/** Axis source for coordinate mapping */
export type AxisSource = 'x' | 'y' | 'z'

/** Axis mapping configuration */
export interface AxisConfig {
  xFrom: AxisSource
  yFrom: AxisSource
  zFrom: AxisSource
  xSign: 1 | -1
  ySign: 1 | -1
  zSign: 1 | -1
}

/** Y-axis offset to float annotations above the mesh surface */
export const ANNOTATION_Y_OFFSET = -0.2

/**
 * Parse world position from annotation format
 */
export function parseWorldPosition(
  pos: WorldPosition
): { x: number; y: number; z: number } | null {
  if (pos.x !== undefined && pos.y !== undefined && pos.z !== undefined) {
    return { x: pos.x, y: pos.y, z: pos.z }
  }
  return null
}

/**
 * Calculate annotation center position in Three.js coordinates
 */
export function getAnnotationCenter(
  annotation: KVAnnotation,
  axisConfig: AxisConfig
): Vector3 | null {
  const mapToThree = (p: { x: number; y: number; z: number }): Vector3 => {
    const getValue = (axis: AxisSource) => {
      switch (axis) {
        case 'x':
          return p.x
        case 'y':
          return p.y
        case 'z':
          return p.z
      }
    }
    return new Vector3(
      getValue(axisConfig.xFrom) * axisConfig.xSign,
      getValue(axisConfig.yFrom) * axisConfig.ySign - ANNOTATION_Y_OFFSET,
      getValue(axisConfig.zFrom) * axisConfig.zSign
    )
  }

  if (!annotation.geometry) return null

  if (annotation.geometry.kind === 'point' && annotation.geometry.position) {
    const pos = parseWorldPosition(annotation.geometry.position)
    if (!pos) return null
    return mapToThree(pos)
  }

  if (annotation.geometry.kind === 'polygon' && annotation.geometry.points) {
    const points = annotation.geometry.points
      .map(parseWorldPosition)
      .filter((p): p is { x: number; y: number; z: number } => p !== null)
      .map(mapToThree)

    if (points.length === 0) return null

    const center = new Vector3()
    points.forEach((p) => center.add(p))
    center.divideScalar(points.length)
    return center
  }

  return null
}
